/* My dogs page: talks to the dog-profiles service through the Shopify app proxy at /apps/dogs.
 *
 * Every rule is enforced server-side; checks here are only a convenience. The UI keys off the
 * error `code`, never the server's `message`. See docs/FRONTEND_BRIEF.md in the dog-profile repo
 * for the API contract.
 */
(function () {
  'use strict';

  var root = document.getElementById('dogs-page');
  if (!root) return;

  var API = '/apps/dogs';
  var MAX_PHOTO_BYTES = 5 * 1024 * 1024;
  var PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
  var PHOTO_EXTENSIONS = {
    jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp',
    heic: 'image/heic', heif: 'image/heif',
  };

  var state = {
    dogs: [],
    limits: { max_dogs: 3, eligibility_days: 7 },
    addOpen: false,
    addPhoto: null,      // File chosen in the add form
    activeEdit: null,    // dog id whose edit form is open
    editDraft: null,     // {name, birthday, breed} typed into the open edit form
    editPhotoStatus: '', // "Photo updated." etc. for the open edit form
    confirming: null,    // dog id whose remove confirm is open
    busy: {},            // dog id -> true while a request for that dog is in flight
    notices: {},         // dog id -> message shown on that dog's card
  };

  function $(id) { return document.getElementById(id); }

  var el = {
    loading: $('d-loading'),
    loadError: $('d-load-error'),
    notEnrolled: $('d-not-enrolled'),
    main: $('d-main'),
    list: $('d-list'),
    empty: $('d-empty'),
    globalErr: $('d-global-error'),
    addTrigger: $('d-add-trigger'),
    capNote: $('d-cap-note'),
    addCard: $('d-add-form-card'),
    addForm: $('d-add-form'),
    addAlert: $('d-add-alert'),
    addName: $('add-name'),
    addNameErr: $('add-name-error'),
    addBirthday: $('add-birthday'),
    addBirthdayErr: $('add-birthday-error'),
    addBreed: $('add-breed'),
    addBreedErr: $('add-breed-error'),
    addPhoto: $('add-photo'),
    addPhotoName: $('add-photo-name'),
    addPhotoClear: $('add-photo-clear'),
    addPhotoErr: $('add-photo-error'),
    addSubmit: $('add-submit'),
    addCancel: $('add-cancel'),
    editTpl: $('edit-tpl'),
  };

  /* ── Small helpers ────────────────────────────────────────── */

  // Builds an element. Text is always set as text, never HTML, so dog names can't inject markup.
  function h(tag, attrs, children) {
    var node = document.createElement(tag);
    Object.keys(attrs || {}).forEach(function (k) {
      var v = attrs[k];
      if (v === null || v === undefined || v === false) return;
      if (k === 'text') node.textContent = v;
      else if (k === 'onclick') node.addEventListener('click', v);
      else node.setAttribute(k, v === true ? '' : v);
    });
    (children || []).forEach(function (c) { if (c) node.appendChild(c); });
    return node;
  }

  function todayIso() {
    var d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' +
      String(d.getDate()).padStart(2, '0');
  }

  function formatDate(iso) {
    if (!iso) return '';
    var p = iso.split('-');
    return new Date(+p[0], +p[1] - 1, +p[2])
      .toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  // "a week" for 7 days, otherwise "N days". The number comes from the API, never hard-coded.
  function waitingPeriod() {
    var n = state.limits.eligibility_days;
    if (n === 7) return 'a week';
    return n === 1 ? 'a day' : n + ' days';
  }

  function setAlert(node, msg) { node.textContent = msg || ''; }

  function setFieldError(input, errNode, msg) {
    input.classList.add('is-error');
    input.setAttribute('aria-invalid', 'true');
    errNode.textContent = msg;
    input.focus();
  }

  function clearFieldError(input, errNode) {
    input.classList.remove('is-error');
    input.removeAttribute('aria-invalid');
    errNode.textContent = '';
  }

  function setBusyLabel(btn, label) {
    if (label) {
      if (!btn.dataset.label) btn.dataset.label = btn.textContent;
      btn.textContent = label;
      btn.disabled = true;
    } else {
      if (btn.dataset.label) btn.textContent = btn.dataset.label;
      delete btn.dataset.label;
      btn.disabled = false;
    }
  }

  function findDog(id) {
    for (var i = 0; i < state.dogs.length; i++) if (state.dogs[i].id === id) return state.dogs[i];
    return null;
  }

  function replaceDog(dog) {
    state.dogs = state.dogs.map(function (d) { return d.id === dog.id ? dog : d; });
  }

  /* ── API ──────────────────────────────────────────────────── */

  // Always resolves to {ok, status, data}. Network failures and non-JSON responses (e.g. a
  // proxy error page) become error codes rather than rejections, so no spinner is left stuck.
  function api(path, method, body) {
    var opts = {
      method: method || 'GET',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' },
    };
    if (body) {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    return fetch(API + path, opts).then(function (res) {
      if (res.status === 204) return { ok: true, status: 204, data: {} };
      return res.json().then(function (data) {
        return { ok: res.ok, status: res.status, data: data || {} };
      }, function () {
        return { ok: false, status: res.status, data: { error: { code: 'bad_response' } } };
      });
    }, function () {
      return { ok: false, status: 0, data: { error: { code: 'network_error' } } };
    });
  }

  function dogPath(id, suffix) { return '/' + encodeURIComponent(id) + (suffix || ''); }

  // Normalises an API result or a thrown client-side error to {code, loginUrl}.
  function errorOf(x) {
    if (x && x.data) {
      var e = x.data.error || {};
      // The service nests login_url inside `error`; accept a top-level one too.
      return { code: e.code || 'unknown', loginUrl: e.login_url || x.data.login_url || null };
    }
    return { code: (x && x.code) || 'unknown', loginUrl: null };
  }

  // Redirect to sign in ONLY when the response carries login_url (invalid_signature never does).
  function redirectIfSignedOut(err) {
    if ((err.code === 'unauthenticated' || err.code === 'stale_request') && err.loginUrl) {
      window.location.href = withReturnToThisPage(err.loginUrl);
      return true;
    }
    return false;
  }

  // The service's login_url returns to the API path (/apps/dogs), which would land the customer on
  // raw JSON after signing in. Point return_to back at this page instead (same-origin path only).
  function withReturnToThisPage(loginUrl) {
    try {
      var url = new URL(loginUrl, window.location.origin);
      if (url.origin !== window.location.origin) return loginUrl;
      url.searchParams.set('return_to', window.location.pathname + window.location.search);
      return url.pathname + url.search;
    } catch (e) {
      return loginUrl;
    }
  }

  function messageFor(code) {
    switch (code) {
      case 'cap_reached':
        return 'You can add up to ' + state.limits.max_dogs + ' dogs.';
      case 'duplicate_dog':
        return 'That dog is already on your list.';
      case 'name_required':
        return 'Please enter a name.';
      case 'birthday_in_future':
        return "The birthday can't be in the future.";
      case 'birthday_too_recent':
        return "Dogs can be added once they've had their first birthday.";
      case 'birthday_too_old':
        return 'Please choose a birthday from 1995 onwards.';
      case 'birthday_locked':
        return "This birthday can't be changed any more. Please refresh the page.";
      case 'not_found':
        return 'That dog has already been removed.';
      case 'invalid_signature':
      case 'unauthenticated':
      case 'stale_request':
        return "We couldn't verify your session. Please refresh the page.";
      case 'too_large':
        return 'That photo is over 5 MB. Please choose a smaller one.';
      case 'invalid_image':
      case 'unsupported_type':
        return "That file isn't a photo we can use. Please choose a JPEG, PNG or HEIC photo.";
      case 'upload_not_found':
        return 'The photo upload expired. Please try again.';
      case 'upload_failed':
        return "The photo didn't upload. Please check your connection and try again.";
      case 'network_error':
        return "We couldn't reach our server. Please check your connection and try again.";
      default:
        return 'Something went wrong. Please try again.';
    }
  }

  /* ── Photos ───────────────────────────────────────────────── */

  // Some browsers give .heic files an empty type, so fall back to the extension.
  function photoType(file) {
    var t = (file.type || '').toLowerCase();
    if (t === 'image/jpg') t = 'image/jpeg';
    if (PHOTO_TYPES.indexOf(t) !== -1) return t;
    if (t) return null;
    var ext = (file.name.split('.').pop() || '').toLowerCase();
    return PHOTO_EXTENSIONS[ext] || null;
  }

  // Returns an error code if the file can't be uploaded, else null.
  function checkPhoto(file) {
    if (!photoType(file)) return 'unsupported_type';
    if (file.size > MAX_PHOTO_BYTES) return 'too_large';
    return null;
  }

  // The three-call upload from the brief: signed URL, PUT straight to storage, then attach.
  // Resolves to the updated dog; rejects with an API result or {code}.
  function uploadPhoto(dogId, file) {
    var bad = checkPhoto(file);
    if (bad) return Promise.reject({ code: bad });
    return api(dogPath(dogId, '/photo/upload-url'), 'POST', { content_type: photoType(file) })
      .then(function (r1) {
        if (!r1.ok) throw r1;
        var up = r1.data;
        // Exactly the returned headers: anything extra breaks the signature.
        return fetch(up.url, { method: 'PUT', headers: up.headers, body: file })
          .then(function (put) {
            if (!put.ok) throw { code: 'upload_failed' };
            return up.upload_id;
          }, function () { throw { code: 'upload_failed' }; });
      })
      .then(function (uploadId) {
        return api(dogPath(dogId, '/photo'), 'POST', { upload_id: uploadId });
      })
      .then(function (r2) {
        if (!r2.ok) throw r2;
        return r2.data.dog;
      });
  }

  /* ── Breed picker ─────────────────────────────────────────── */

  // Breeds come from assets/my-dogs-breeds.js. If that file didn't load, the picker falls back to
  // accepting any text rather than blocking the form.
  var BREEDS = Array.isArray(window.MyDogsBreeds) ? window.MyDogsBreeds : [];
  var OTHER_BREED = 'Other';

  // "Pit-bull" and "pitbull" both become "pitbull", so spacing and punctuation don't matter.
  function breedKey(s) { return String(s || '').toLowerCase().replace(/[^a-z0-9]/g, ''); }

  var BREED_BY_KEY = {};
  BREEDS.concat([OTHER_BREED]).forEach(function (b) { BREED_BY_KEY[breedKey(b)] = b; });

  // Every typed word must appear in the breed ("lab black" finds "Black Labrador Retriever").
  // Breeds starting with the query come first, then ones with a word starting with it.
  function matchBreeds(query) {
    var words = String(query || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    if (!words.length) return BREEDS.slice();
    var q = breedKey(query);
    var ranked = [[], [], []];
    BREEDS.forEach(function (b) {
      var key = breedKey(b);
      for (var i = 0; i < words.length; i++) if (key.indexOf(words[i]) === -1) return;
      if (key.indexOf(q) === 0) ranked[0].push(b);
      else if (b.toLowerCase().split(/[^a-z0-9]+/).some(function (w) { return w.indexOf(words[0]) === 0; })) ranked[1].push(b);
      else ranked[2].push(b);
    });
    return ranked[0].concat(ranked[1], ranked[2]);
  }

  // Turns a text input + empty listbox into a searchable breed dropdown (ARIA 1.2 combobox).
  // `legacy` is a dog's saved breed from before the list existed; it stays valid while unchanged.
  // Returns {value, close}: value() is the chosen breed, '' when empty, or null when the text
  // isn't a breed on the list.
  function breedPicker(input, list, legacy) {
    var options = [];
    var active = -1;
    var idBase = list.id || input.id + '-list';
    list.id = idBase;
    input.setAttribute('aria-controls', idBase);

    // `showAll` lists every breed (reopening a filled field) instead of filtering by its text.
    function open(showAll) {
      var matches = matchBreeds(showAll ? '' : input.value);
      options = matches.concat([OTHER_BREED]);
      list.textContent = '';
      if (!matches.length) {
        list.appendChild(h('li', { class: 'd-combo-empty', role: 'presentation',
          text: 'No breeds match. Choose Other if yours isn’t listed.' }));
      }
      options.forEach(function (b, i) {
        list.appendChild(h('li', {
          id: idBase + '-' + i, role: 'option', 'aria-selected': 'false', 'data-index': i,
          class: 'd-combo-option' + (b === OTHER_BREED ? ' is-other' : ''), text: b,
        }));
      });
      list.hidden = false;
      list.scrollTop = 0;
      input.setAttribute('aria-expanded', 'true');
      // Highlight the current breed, or the first match while typing, so Enter picks it.
      var current = options.indexOf(resolve(input.value));
      if (showAll) setActive(current);
      else setActive(input.value.trim() && matches.length ? 0 : -1);
    }

    function close() {
      list.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      active = -1;
    }

    function setActive(i) {
      var prev = list.querySelector('[aria-selected="true"]');
      if (prev) prev.setAttribute('aria-selected', 'false');
      active = i;
      if (i < 0) { input.removeAttribute('aria-activedescendant'); return; }
      var node = list.querySelector('[data-index="' + i + '"]');
      node.setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', node.id);
      node.scrollIntoView({ block: 'nearest' });
    }

    function choose(i) {
      input.value = options[i];
      close();
      // Lets the edit form's draft and error-clearing see the new value.
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    function resolve(text) {
      var t = text.trim();
      if (!t) return '';
      if (!BREEDS.length || (legacy && t === legacy)) return t;
      return BREED_BY_KEY[breedKey(t)] || null;
    }

    // isTrusted: only real typing opens the list, not the events choose() dispatches.
    input.addEventListener('input', function (e) { if (e.isTrusted) open(); });
    // Selecting the text means typing replaces the chosen breed rather than appending to it.
    input.addEventListener('click', function () {
      if (!list.hidden) return;
      open(true);
      input.select();
    });
    input.addEventListener('keydown', function (e) {
      var isOpen = !list.hidden;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (!isOpen) return open(true);
        var n = options.length;
        setActive(e.key === 'ArrowDown' ? (active + 1) % n : (active <= 0 ? n - 1 : active - 1));
      } else if (e.key === 'Enter' && isOpen) {
        e.preventDefault(); // pick the breed, don't submit the form
        if (active >= 0) choose(active);
        else close();
      } else if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        close();
      }
    });

    // mousedown would blur the input before the click lands, so keep focus in the input.
    list.addEventListener('mousedown', function (e) { e.preventDefault(); });
    list.addEventListener('click', function (e) {
      var opt = e.target.closest('[role="option"]');
      if (opt) choose(+opt.getAttribute('data-index'));
    });

    // Leaving the field tidies an exact match ("golden retriever") to the list's spelling.
    input.addEventListener('blur', function () {
      close();
      var v = resolve(input.value);
      if (v && v !== input.value) {
        input.value = v;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    });

    return { value: function () { return resolve(input.value); }, close: close };
  }

  var BREED_NOT_LISTED = 'Please choose a breed from the list, or Other if yours isn’t there.';

  /* ── Rendering ────────────────────────────────────────────── */

  function render() {
    var dogs = state.dogs;
    var max = state.limits.max_dogs;
    // dogs can exceed max_dogs (see the brief), so compare with >= rather than ===.
    var atCap = dogs.length >= max;

    el.empty.hidden = dogs.length > 0;
    el.addTrigger.hidden = atCap;
    el.capNote.hidden = !atCap;
    el.capNote.textContent = 'You can add up to ' + max + ' dogs.';
    if (atCap && state.addOpen) closeAddForm();

    el.list.textContent = '';
    dogs.forEach(function (dog) { el.list.appendChild(renderCard(dog)); });
  }

  function renderCard(dog) {
    var busy = !!state.busy[dog.id];
    var meta = [dog.birthday && formatDate(dog.birthday), dog.breed].filter(Boolean).join(' · ');

    var photo = h('div', { class: 'dog-photo' });
    if (dog.photo_url) {
      photo.appendChild(h('img', { src: dog.photo_url, alt: 'Photo of ' + dog.name, loading: 'lazy' }));
    } else {
      photo.appendChild(h('span', { 'aria-hidden': 'true', text: (dog.name || '?').trim().charAt(0).toUpperCase() }));
    }

    var info = h('div', { class: 'dog-info' }, [
      h('p', { class: 'dog-name', text: dog.name }),
      meta ? h('p', { class: 'dog-meta', text: meta }) : null,
      dog.earns_this_year === false ? h('p', {
        class: 'dog-waiting',
        text: dog.name + ' was added less than ' + waitingPeriod() + ' before their birthday, ' +
          'so their first reward will be on the birthday after this one.',
      }) : null,
    ]);

    var editing = state.activeEdit === dog.id;
    var btns = h('div', { class: 'dog-btns' }, [
      h('button', {
        class: 'dp-btn dp-btn-ghost dp-btn-sm', type: 'button', disabled: busy,
        'aria-expanded': editing ? 'true' : 'false', text: editing ? 'Close' : 'Edit',
        onclick: function () { toggleEdit(dog.id); },
      }),
      h('button', {
        class: 'dp-btn dp-btn-ghost dp-btn-sm', type: 'button', disabled: busy,
        text: 'Remove', onclick: function () { toggleConfirm(dog.id); },
      }),
    ]);

    var card = h('div', { class: 'd-card', 'data-dog-id': dog.id }, [
      h('div', { class: 'dog-row' }, [photo, info, btns]),
    ]);

    if (state.notices[dog.id]) {
      card.appendChild(h('div', { class: 'd-alert d-alert-error dog-card-alert', role: 'alert', text: state.notices[dog.id] }));
    }

    if (state.confirming === dog.id) {
      card.appendChild(h('div', { class: 'dog-confirm' }, [
        h('p', { class: 'dog-confirm-msg', text: 'Remove ' + dog.name + "? This can't be undone." }),
        h('div', { class: 'dog-confirm-actions' }, [
          h('button', {
            class: 'dp-btn dp-btn-danger dp-btn-sm', type: 'button', disabled: busy,
            text: busy ? 'Removing…' : 'Remove', onclick: function () { removeDog(dog.id); },
          }),
          h('button', {
            class: 'dp-btn dp-btn-outline dp-btn-sm', type: 'button', disabled: busy,
            text: 'Cancel', onclick: function () { state.confirming = null; render(); },
          }),
        ]),
      ]));
    }

    if (editing) card.appendChild(renderEditForm(dog));
    return card;
  }

  function renderEditForm(dog) {
    var frag = el.editTpl.content.cloneNode(true);
    var q = function (sel) { return frag.querySelector(sel); };
    var wrap = q('.edit-wrap');
    var alertNode = q('.edit-alert');
    var form = q('.edit-form');
    var name = q('.edit-name');
    var nameErr = q('.edit-name-error');
    var bday = q('.edit-birthday');
    var bdayErr = q('.edit-birthday-error');
    var breed = q('.edit-breed');
    var breedErr = q('.edit-breed-error');
    var pick = q('.edit-photo-pick');
    var fileInput = q('.edit-photo-input');
    var removePhotoBtn = q('.edit-photo-remove');
    var photoStatus = q('.edit-photo-status');
    var photoErr = q('.edit-photo-error');
    var submit = q('.edit-submit');
    var busy = !!state.busy[dog.id];

    // Unique ids so every label is tied to its input.
    var uid = 'edit-' + String(dog.id).replace(/[^A-Za-z0-9_-]/g, '');
    [['.js-for-name', name, 'name'], ['.js-for-birthday', bday, 'birthday'],
     ['.js-for-breed', breed, 'breed']].forEach(function (x) {
      x[1].id = uid + '-' + x[2];
      q(x[0]).setAttribute('for', x[1].id);
    });
    fileInput.id = uid + '-photo';
    pick.setAttribute('for', fileInput.id);

    var draft = state.editDraft || { name: dog.name || '', birthday: dog.birthday || '', breed: dog.breed || '' };
    name.value = draft.name;
    bday.value = draft.birthday;
    breed.value = draft.breed;
    bday.max = todayIso();
    var breedChoice = breedPicker(breed, q('.edit-breed-list'), dog.breed || '');

    if (dog.birthday_locked) {
      bday.disabled = true;
      bday.value = dog.birthday || '';
      q('.edit-birthday-hint').hidden = true;
      q('.edit-locked-note').hidden = false;
    }

    // Keep what's typed if the card re-renders (e.g. after a photo upload).
    form.addEventListener('input', function () {
      state.editDraft = { name: name.value, birthday: bday.value, breed: breed.value };
    });

    pick.textContent = dog.photo_url ? 'Change photo' : 'Add photo';
    removePhotoBtn.hidden = !dog.photo_url;
    photoStatus.textContent = busy ? '' : state.editPhotoStatus;
    if (busy) {
      pick.setAttribute('aria-disabled', 'true');
      fileInput.disabled = true;
      removePhotoBtn.disabled = true;
      submit.disabled = true;
    }

    fileInput.addEventListener('change', function () {
      var file = fileInput.files && fileInput.files[0];
      fileInput.value = '';
      if (!file) return;
      var bad = checkPhoto(file);
      if (bad) { photoErr.textContent = messageFor(bad); return; }
      photoErr.textContent = '';
      changePhoto(dog.id, file);
    });

    removePhotoBtn.addEventListener('click', function () {
      photoErr.textContent = '';
      removePhoto(dog.id);
    });

    q('.edit-cancel').addEventListener('click', function () { closeEdit(); render(); });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      setAlert(alertNode, '');
      clearFieldError(name, nameErr);
      clearFieldError(bday, bdayErr);
      clearFieldError(breed, breedErr);

      var body = {};
      var newName = name.value.trim();
      var newBreed = breedChoice.value();
      if (!newName) { setFieldError(name, nameErr, messageFor('name_required')); return; }
      if (newBreed === null) { setFieldError(breed, breedErr, BREED_NOT_LISTED); return; }
      if (newName !== (dog.name || '')) body.name = newName;
      if (!dog.birthday_locked && bday.value !== (dog.birthday || '')) {
        if (!bday.value) { setFieldError(bday, bdayErr, 'Please enter a birthday.'); return; }
        body.birthday = bday.value;
      }
      if (newBreed !== (dog.breed || '')) body.breed = newBreed;

      if (Object.keys(body).length === 0) { closeEdit(); render(); return; }

      setBusyLabel(submit, 'Saving…');
      api(dogPath(dog.id), 'PATCH', body).then(function (result) {
        setBusyLabel(submit, null);
        if (result.ok) {
          replaceDog(result.data.dog);
          closeEdit();
          render();
          return;
        }
        var err = errorOf(result);
        if (redirectIfSignedOut(err)) return;
        if (err.code === 'not_found') return reloadWithNotice(messageFor('not_found'));
        if (err.code === 'name_required') return setFieldError(name, nameErr, messageFor(err.code));
        if (/^birthday_(in_future|too_recent|too_old)$/.test(err.code)) {
          return setFieldError(bday, bdayErr, messageFor(err.code));
        }
        setAlert(alertNode, messageFor(err.code));
      });
    });

    return wrap;
  }

  /* ── Dog actions ──────────────────────────────────────────── */

  function toggleEdit(id) {
    setAlert(el.globalErr, '');
    if (state.activeEdit === id) {
      closeEdit();
    } else {
      closeEdit();
      state.activeEdit = id;
      state.confirming = null;
      if (state.addOpen) closeAddForm();
    }
    render();
  }

  function closeEdit() {
    state.activeEdit = null;
    state.editDraft = null;
    state.editPhotoStatus = '';
  }

  function toggleConfirm(id) {
    setAlert(el.globalErr, '');
    state.confirming = state.confirming === id ? null : id;
    render();
  }

  function removeDog(id) {
    state.busy[id] = true;
    render();
    api(dogPath(id), 'DELETE').then(function (result) {
      delete state.busy[id];
      var err = result.ok ? null : errorOf(result);
      if (err && redirectIfSignedOut(err)) return;
      if (!err || err.code === 'not_found') {
        // Gone either way: drop it from the list.
        state.dogs = state.dogs.filter(function (d) { return d.id !== id; });
        delete state.notices[id];
        state.confirming = null;
        if (state.activeEdit === id) closeEdit();
        render();
        return;
      }
      render();
      setAlert(el.globalErr, messageFor(err.code));
    });
  }

  function changePhoto(id, file) {
    state.busy[id] = true;
    state.editPhotoStatus = '';
    render();
    // render() rebuilt the form, so the status line to update is the new one.
    var status = el.list.querySelector('[data-dog-id="' + CSS.escape(id) + '"] .edit-photo-status');
    if (status) status.textContent = 'Uploading photo…';

    uploadPhoto(id, file).then(function (dog) {
      delete state.busy[id];
      delete state.notices[id];
      replaceDog(dog);
      state.editPhotoStatus = 'Photo updated.';
      render();
    }, function (e) {
      delete state.busy[id];
      var err = errorOf(e);
      if (redirectIfSignedOut(err)) return;
      if (err.code === 'not_found') return reloadWithNotice(messageFor('not_found'));
      render();
      showEditPhotoError(id, messageFor(err.code));
    });
  }

  function removePhoto(id) {
    state.busy[id] = true;
    state.editPhotoStatus = '';
    render();
    api(dogPath(id, '/photo'), 'DELETE').then(function (result) {
      delete state.busy[id];
      if (result.ok) {
        var dog = findDog(id);
        if (dog) replaceDog(Object.assign({}, dog, { photo_url: null }));
        state.editPhotoStatus = 'Photo removed.';
        render();
        return;
      }
      var err = errorOf(result);
      if (redirectIfSignedOut(err)) return;
      if (err.code === 'not_found') return reloadWithNotice(messageFor('not_found'));
      render();
      showEditPhotoError(id, messageFor(err.code));
    });
  }

  function showEditPhotoError(id, msg) {
    var node = el.list.querySelector('[data-dog-id="' + CSS.escape(id) + '"] .edit-photo-error');
    if (node) node.textContent = msg;
  }

  /* ── Add form ─────────────────────────────────────────────── */

  function openAddForm() {
    closeEdit();
    state.confirming = null;
    state.addOpen = true;
    el.addTrigger.classList.add('is-form-open');
    el.addTrigger.setAttribute('aria-expanded', 'true');
    el.addCard.hidden = false;
    render();
    el.addName.focus();
  }

  function closeAddForm() {
    state.addOpen = false;
    el.addTrigger.classList.remove('is-form-open');
    el.addTrigger.setAttribute('aria-expanded', 'false');
    el.addCard.hidden = true;
    el.addForm.reset();
    setAddPhoto(null);
    setAlert(el.addAlert, '');
    clearFieldError(el.addName, el.addNameErr);
    clearFieldError(el.addBirthday, el.addBirthdayErr);
    clearFieldError(el.addBreed, el.addBreedErr);
    addBreedChoice.close();
  }

  function setAddPhoto(file) {
    state.addPhoto = file;
    el.addPhotoName.textContent = file ? file.name : '';
    el.addPhotoClear.hidden = !file;
    el.addPhotoErr.textContent = '';
    if (!file) el.addPhoto.value = '';
  }

  var addBreedChoice = breedPicker(el.addBreed, $('add-breed-list'), '');

  el.addTrigger.addEventListener('click', function () { if (!state.addOpen) openAddForm(); });
  el.addCancel.addEventListener('click', closeAddForm);
  el.addPhotoClear.addEventListener('click', function () { setAddPhoto(null); });

  el.addPhoto.addEventListener('change', function () {
    var file = el.addPhoto.files && el.addPhoto.files[0];
    if (!file) return setAddPhoto(null);
    var bad = checkPhoto(file);
    if (bad) {
      setAddPhoto(null);
      el.addPhotoErr.textContent = messageFor(bad);
      return;
    }
    setAddPhoto(file);
  });

  el.addForm.addEventListener('submit', function (e) {
    e.preventDefault();
    setAlert(el.addAlert, '');
    clearFieldError(el.addName, el.addNameErr);
    clearFieldError(el.addBirthday, el.addBirthdayErr);
    clearFieldError(el.addBreed, el.addBreedErr);

    var name = el.addName.value.trim();
    if (!name) return setFieldError(el.addName, el.addNameErr, messageFor('name_required'));
    if (!el.addBirthday.value) return setFieldError(el.addBirthday, el.addBirthdayErr, 'Please enter a birthday.');
    var breed = addBreedChoice.value();
    if (breed === null) return setFieldError(el.addBreed, el.addBreedErr, BREED_NOT_LISTED);

    var body = { name: name, birthday: el.addBirthday.value };
    if (breed) body.breed = breed;
    var photo = state.addPhoto;

    setBusyLabel(el.addSubmit, 'Saving…');
    el.addCancel.disabled = true;

    api('', 'POST', body).then(function (result) {
      if (!result.ok) {
        setBusyLabel(el.addSubmit, null);
        el.addCancel.disabled = false;
        var err = errorOf(result);
        if (redirectIfSignedOut(err)) return;
        if (err.code === 'name_required') return setFieldError(el.addName, el.addNameErr, messageFor(err.code));
        if (/^birthday_(in_future|too_recent|too_old)$/.test(err.code)) {
          return setFieldError(el.addBirthday, el.addBirthdayErr, messageFor(err.code));
        }
        return setAlert(el.addAlert, messageFor(err.code));
      }

      var dog = result.data.dog;
      state.dogs.push(dog);
      if (!photo) return finishAdd();

      // The dog exists now; the photo attaches to its id. A failed photo doesn't undo the add.
      el.addSubmit.textContent = 'Uploading photo…';
      return uploadPhoto(dog.id, photo).then(function (updated) {
        replaceDog(updated);
        finishAdd();
      }, function (e2) {
        var err2 = errorOf(e2);
        if (redirectIfSignedOut(err2)) return;
        state.notices[dog.id] = dog.name + " was added, but the photo didn't upload. " +
          messageFor(err2.code) + ' You can add it with Edit.';
        finishAdd();
      });
    });
  });

  function finishAdd() {
    setBusyLabel(el.addSubmit, null);
    el.addCancel.disabled = false;
    closeAddForm();
    render();
  }

  /* ── Load ─────────────────────────────────────────────────── */

  function reloadWithNotice(msg) {
    closeEdit();
    state.confirming = null;
    loadDogs().then(function () { setAlert(el.globalErr, msg); });
  }

  function loadDogs() {
    el.loading.hidden = false;
    el.main.hidden = true;
    el.notEnrolled.hidden = true;
    el.loadError.hidden = true;

    return api('', 'GET').then(function (result) {
      el.loading.hidden = true;

      if (!result.ok) {
        var err = errorOf(result);
        if (redirectIfSignedOut(err)) {
          el.loading.hidden = false;
          return;
        }
        el.loadError.textContent = err.code === 'network_error'
          ? messageFor('network_error')
          : "We couldn't load your dogs. Please refresh the page to try again.";
        el.loadError.hidden = false;
        return;
      }

      var data = result.data;
      state.limits = data.limits || state.limits;
      updateWaitingHint();

      // false: not a member, so invite them to join. The list stays usable: a dog added now
      // counts towards eligibility from today, so it earns once they've joined.
      // null: couldn't check, say nothing.
      el.notEnrolled.hidden = !(data.loyalty && data.loyalty.enrolled === false);

      state.dogs = data.dogs || [];
      el.main.hidden = false;
      render();
    });
  }

  function updateWaitingHint() {
    root.querySelector('.js-waiting-hint').textContent =
      'Add them at least ' + waitingPeriod() + ' out so their reward is ready in time.';
  }

  // The theme's header overlays the page; pad the page by its real height.
  function fitUnderHeader() {
    var header = document.querySelector('sticky-header, .section-header header');
    if (header) root.style.setProperty('--dp-header-h', header.offsetHeight + 'px');
  }
  fitUnderHeader();
  window.addEventListener('resize', fitUnderHeader);

  el.addBirthday.max = todayIso();
  updateWaitingHint();
  loadDogs();
}());
